import { cn } from "@/lib/utils";
import { format, isToday, type Locale } from "date-fns";
import { formatTime } from "@/lib/dates";
import { useTranslate } from "ra-core";
import { Link } from "react-router";

import { AgendaDayStrip } from "./AgendaDay";
import { dayKey } from "./mobileModel";
import { SOURCE_BAR, type AgendaEntry } from "./useAgendaEntries";

type WeekProps = {
  days: Date[];
  byDay: Map<string, AgendaEntry[]>;
  dfLocale: Locale;
  locale: string;
  onOpenDay: (day: Date) => void;
};

const sectionId = (day: Date) => `agenda-week-${dayKey(day)}`;

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/**
 * Phone: the compact day strip jumps to one of seven stacked day sections.
 * md+: seven columns side by side. Day headers open that day in Day view.
 */
export const AgendaWeek = ({
  days,
  byDay,
  dfLocale,
  locale,
  onOpenDay,
}: WeekProps) => {
  const busyKeys = new Set(byDay.keys());
  const scrollToDay = (day: Date) =>
    document.getElementById(sectionId(day))?.scrollIntoView({
      behavior: prefersReducedMotion() ? "auto" : "smooth",
      block: "start",
    });

  return (
    <>
      <div className="md:hidden">
        <AgendaDayStrip
          days={days}
          selected={null}
          busyKeys={busyKeys}
          dfLocale={dfLocale}
          onSelect={scrollToDay}
        />
        <div className="flex flex-col gap-5">
          {days.map((day) => (
            <section
              key={dayKey(day)}
              id={sectionId(day)}
              className="scroll-mt-[calc(var(--mobile-content-top)+4rem)]"
            >
              <DayHeading day={day} dfLocale={dfLocale} onOpen={onOpenDay} />
              <DayItems
                entries={byDay.get(dayKey(day)) ?? []}
                locale={locale}
              />
            </section>
          ))}
        </div>
      </div>
      <div className="hidden grid-cols-7 gap-2 md:grid">
        {days.map((day) => (
          <section
            key={dayKey(day)}
            className={cn(
              "flex min-h-[55vh] min-w-0 flex-col gap-2 rounded-3xl bg-card/60 p-2 ring-1 ring-border",
              isToday(day) && "bg-card ring-brand/40 dark:ring-foreground/30",
            )}
          >
            <DayHeading
              day={day}
              dfLocale={dfLocale}
              onOpen={onOpenDay}
              compact
            />
            <DayItems
              entries={byDay.get(dayKey(day)) ?? []}
              locale={locale}
              compact
            />
          </section>
        ))}
      </div>
    </>
  );
};

const DayHeading = ({
  day,
  dfLocale,
  onOpen,
  compact = false,
}: {
  day: Date;
  dfLocale: Locale;
  onOpen: (day: Date) => void;
  compact?: boolean;
}) => {
  const translate = useTranslate();
  const today = isToday(day);
  return (
    <button
      type="button"
      onClick={() => onOpen(day)}
      aria-label={format(day, "PPPP", { locale: dfLocale })}
      className={cn(
        "flex items-center gap-2 rounded-2xl text-left outline-none focus-visible:ring-2 focus-visible:ring-ring",
        compact
          ? "flex-col gap-0.5 px-1 py-1.5"
          : "mb-2 w-full px-1 py-0.5 text-sm",
      )}
    >
      <span
        className={cn(
          "text-xs font-medium tracking-wide text-muted-foreground uppercase",
          !compact && "w-9",
        )}
      >
        {format(day, "EEE", { locale: dfLocale }).replace(".", "")}
      </span>
      <span
        className={cn(
          "inline-flex size-7 items-center justify-center rounded-full font-semibold",
          today && "agenda-accent",
        )}
      >
        {format(day, "d")}
      </span>
      {today && !compact ? (
        <span className="text-xs font-medium text-brand dark:text-foreground">
          {translate("crm.mobile.agenda_today")}
        </span>
      ) : null}
    </button>
  );
};

const DayItems = ({
  entries,
  locale,
  compact = false,
}: {
  entries: AgendaEntry[];
  locale: string;
  compact?: boolean;
}) => {
  const translate = useTranslate();
  if (entries.length === 0) {
    return (
      <p
        className={cn(
          "text-muted-foreground/80",
          compact ? "px-1 text-xs" : "pl-12 text-sm",
        )}
      >
        {compact ? "—" : translate("crm.mobile.no_events")}
      </p>
    );
  }
  return (
    <ul className={cn("flex flex-col", compact ? "gap-1" : "gap-1.5 pl-11")}>
      {entries.map((entry) => (
        <li key={entry.id}>
          <AgendaChip entry={entry} locale={locale} compact={compact} />
        </li>
      ))}
    </ul>
  );
};

/** One-line item: source bar, time (or "all day") and title. */
export const AgendaChip = ({
  entry,
  locale,
  compact = false,
}: {
  entry: AgendaEntry;
  locale: string;
  compact?: boolean;
}) => {
  const translate = useTranslate();
  const time = entry.allDay
    ? translate("crm.mobile.all_day")
    : formatTime(entry.start, locale);
  const body = (
    <>
      <span
        className={cn(
          "w-1 shrink-0 self-stretch rounded-full",
          SOURCE_BAR[entry.source] ?? "bg-chart-1",
        )}
      />
      <span
        className={cn(
          "min-w-0 flex-1",
          compact ? "flex flex-col" : "flex items-baseline gap-2",
        )}
      >
        <span
          className={cn(
            "shrink-0 text-muted-foreground tabular-nums",
            compact ? "text-[11px]" : "text-xs",
          )}
        >
          {time}
        </span>
        <span
          className={cn(
            "font-medium",
            compact ? "line-clamp-2 text-xs" : "truncate text-sm",
            entry.done && "text-muted-foreground line-through",
          )}
        >
          {entry.title}
        </span>
      </span>
    </>
  );
  const className = cn(
    "flex items-stretch gap-2 rounded-2xl bg-card shadow-card ring-1 ring-border outline-none focus-visible:ring-2 focus-visible:ring-ring",
    compact ? "p-1.5" : "px-3 py-2.5",
  );
  if (!entry.href) return <div className={className}>{body}</div>;
  return (
    <Link to={entry.href} className={className}>
      {body}
    </Link>
  );
};
